<template>
    <BasicHeader pretitle="Credentials" :title="credentialName" :buttonText="editMode ? 'Save' : ''"
        :buttonDisabled="saving || !validateCredential" :buttonLoading="saving" :buttonRoles="allowedUserRoles"
        :ignoreButtonLift="true" buttonClass="btn-success" @btnClick="onSaveClick"
        :buttonText2="credentialFound ? (editMode ? 'Delete' : 'Edit') : ''"
        :buttonClass2="editMode ? 'btn-danger' : 'btn-primary'" :button2Roles="allowedUserRoles"
        @btnClick2="editMode ? onDeleteClick() : setEditMode(true)" :ignoreButtonLift2="true"
        :buttonIconClass3="editMode ? '' : 'fa fa-arrow-left me-1'"
        :buttonText3="editMode ? 'Cancel' : 'Back to Credentials list'"
        @btnClick3="editMode ? setEditMode(false) : goToList()" :ignoreButtonLift3="true" />
    <div class="container-fluid">
        <div v-if="error" class="alert alert-danger" role="alert">
            {{ error }}
        </div>
        <div v-if="loading" class="text-center">
            <div class="spinner-border">
                <span class="visually-hidden">Loading...</span>
            </div>
        </div>
        <div v-else-if="!credentialFound && !error" class="alert alert-secondary" role="alert">
            Credential <strong>{{ credentialName }}</strong> was not found under
            <strong>{{ service }}</strong> / <strong>{{ namespace }}</strong>.
        </div>
        <div v-else class="row">
            <div class="col">
                <div class="card">
                    <div class="card-header">
                        <h4 class="card-header-title">Credential Details</h4>
                        <div class="d-flex">
                            <div class="border-end pe-2 me-2">
                                <span class="small me-1">Service</span>
                                <span class="badge bg-secondary me-2" style="font-family: ui-monospace, monospace;">
                                    {{ service }}
                                </span>
                            </div>
                            <div>
                                <span class="small ms-1 me-1">{{ serviceConfig.namespaceLabel }}</span>
                                <span class="badge bg-secondary" style="font-family: ui-monospace, monospace;">
                                    {{ namespace }}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div class="card-body">
                        <FormFields :fields="credFields" v-model="credential" :inlineField="true"
                            :editMode="editMode" />
                        <div v-if="editMode" class="form-text mb-0">
                            Secrets are never returned by the API - re-enter them to save your changes.
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import BasicHeader from "../../components/BasicHeader.vue";
import FormFields from "../../components/FormFields.vue";
import { mapActions, mapState } from "vuex";
import sandboxAquacredCredentials from "../../utils/field-constants/sandboxAquacredCredentials";
import {
    clearSensitiveFields,
    getCredentialType,
    getServiceConfig,
    hasRequiredFields,
    maskSensitiveFields,
} from "../../utils/aquacred";

export default {
    name: "AquacredCredential",
    components: { BasicHeader, FormFields },
    data() {
        return {
            credential: {},
            snapshot: {}, // original values, restored when an edit is cancelled
            credentialFound: false,
            credFieldDefinitions: JSON.parse(JSON.stringify(sandboxAquacredCredentials)),
            editMode: false,
            loading: false,
            saving: false,
            error: null,
            allowedUserRoles: [1, 1.5, 3], // aqfer admin, aqfer manager or client admin
        };
    },
    computed: {
        ...mapState({
            aquacredCredentials: (state) => state.amdp.aquacredCredentials,
        }),
        // everything the page needs comes from the URL, so it renders identically
        // when the link is opened in another tab or by another user
        cid() {
            return this.$route.params.cid || this.$store.state.currentClient.cid;
        },
        service() {
            return this.$route.params.service;
        },
        namespace() {
            return this.$route.params.namespace;
        },
        credentialName() {
            return this.$route.params.id;
        },
        // credentials cached for this service/namespace, populated by the list page
        cachedCredentials() {
            return this.aquacredCredentials?.[this.service]?.[this.namespace]?.credentials || [];
        },
        serviceConfig() {
            return getServiceConfig(this.service);
        },
        credFields() {
            return this.credFieldDefinitions.map((field) => {
                return {
                    ...field,
                    visible: field.filterByType.includes(this.credential.type),
                };
            });
        },
        validateCredential() {
            return hasRequiredFields(this.credential);
        },
    },
    watch: {
        // Reload when navigating straight from one credential to another, or when the
        // user edits the service/namespace/id in the URL. `deep` is required: the
        // router reuses the params object across same-route navigations.
        // The store decides whether an API call is needed, so switching credentials
        // inside an already-fetched namespace resolves from the cache.
        "$route.params": {
            deep: true,
            handler(params, oldParams) {
                if (params.service === oldParams?.service
                    && params.namespace === oldParams?.namespace
                    && params.id === oldParams?.id) return;
                this.editMode = false;
                this.loadCredential();
            },
        },
    },
    methods: {
        ...mapActions({
            showModal: "showModal",
            fetchAquacredCredentials: "amdp/fetchAquacredCredentials",
            upsertAquacredCredential: "amdp/upsertAquacredCredential",
            deleteAquacredCredentials: "amdp/deleteAquacredCredentials",
        }),
        // `forceGet` is passed to the store, which only calls the API when the
        // (service, namespace) pair is uncached or a refetch is explicitly asked
        // for - so arriving from the list page reuses the list's fetch.
        async loadCredential(forceGet = false) {
            if (!this.service || !this.namespace) return;
            this.error = null;
            this.loading = true;
            try {
                await this.fetchAquacredCredentials({
                    service: this.service,
                    namespace: this.namespace,
                    mutation: "setAquacredCredentials",
                    forceGet,
                });
                const match = this.cachedCredentials.find(cred => cred.name === this.credentialName);
                this.credentialFound = !!match;
                this.credential = maskSensitiveFields(match ? { ...match, type: getCredentialType(match) } : {});
                this.snapshot = JSON.parse(JSON.stringify(this.credential));
            } catch (error) {
                this.error = error?.response?.data?.error ?? error.message;
            } finally {
                this.loading = false;
            }
        },
        setEditMode(value) {
            this.editMode = value;
            if (value) {
                this.snapshot = JSON.parse(JSON.stringify(this.credential));
                // the API never returns secrets, so they have to be re-entered
                clearSensitiveFields(this.credential);
            } else {
                this.credential = JSON.parse(JSON.stringify(this.snapshot));
                maskSensitiveFields(this.credential);
            }
        },
        buildPayload() {
            let payload = {
                name: this.credential.name,
                type: this.credential.type,
            };
            this.credFields.forEach((field) => {
                if (field.visible && this.credential[field.name]) {
                    payload[field.name] = this.credential[field.name];
                }
            });
            return payload;
        },
        async onSaveClick() {
            this.saving = true;
            try {
                await this.upsertAquacredCredential({
                    service: this.service,
                    namespace: this.namespace,
                    credentialName: this.credentialName,
                    payload: this.buildPayload(),
                });
                this.$store.dispatch("addToast", {
                    message: "Credential updated successfully.",
                    type: "success",
                });
                this.editMode = false;
                await this.loadCredential(true);
            } catch (error) {
                let err = error?.response?.data?.error || error?.message;
                console.error(err);
                this.$store.dispatch("addToast", {
                    message: "Something went wrong while updating credential. Please contact support.",
                    type: "error",
                });
            } finally {
                this.saving = false;
            }
        },
        onDeleteClick() {
            let self = this;
            this.showModal({
                component: "confirm",
                data: {
                    title: "Delete Credential",
                    message: `Are you sure you want to <strong class="text-danger">DELETE</strong> the credential
                        - <strong>${self.credentialName}</strong> from <strong>${self.service}</strong> / <strong>${self.namespace}</strong>?`,
                    confirmText: "Delete",
                    confirmClass: "btn-danger",
                    cancelText: "Cancel",
                    cancelClass: "btn-secondary",
                    async confirm() {
                        try {
                            await self.deleteAquacredCredentials({
                                service: self.service,
                                namespace: self.namespace,
                                credentialName: self.credentialName,
                            });
                            self.$store.dispatch("addToast", {
                                message: "Credential deleted successfully.",
                                type: "success",
                            });
                            // the cached list still holds the deleted credential -
                            // refresh it so the list page does not render it
                            await self.fetchAquacredCredentials({
                                service: self.service,
                                namespace: self.namespace,
                                mutation: "setAquacredCredentials",
                                forceGet: true,
                            }).catch(() => { });
                            self.goToList();
                        } catch (error) {
                            let err = error?.response?.data?.error || error?.message;
                            console.error(err);
                            self.$store.dispatch("addToast", {
                                type: "error",
                                message: "Something went wrong while deleting credential. Please contact support.",
                            });
                        }
                    },
                },
            });
        },
        goToList() {
            this.$router.push({
                name: "Aquacred Credentials",
                params: { cid: this.cid },
                query: { service: this.service, namespace: this.namespace },
            });
        },
    },
    beforeMount() {
        // reuses the list page's fetch when it is already cached
        this.loadCredential();
    },
};
</script>

<style lang="scss">
@use '../../theme/scss/_user-variables.scss' as *;

body.dark-mode {
    .badge.bg-secondary {
        background: #424447 !important;
    }
}
</style>
